import type { ProductMarketType } from "~/domains/product/types/ProductData";

export type BulkUploadMode = "ordinary" | "merge";
export type BulkUploadSource = "file" | "text" | "merge";
export type OrdinaryBulkUploadSource = Exclude<BulkUploadSource, "merge">;
export type BulkUploadTemplateType = "csv" | "xlsx";

export type BulkUploadOutcome =
  | "PREVIEW"
  | "UPLOAD_ERROR"
  | "VALIDATION_FAILED"
  | "CONTEXT_CHANGED"
  | "SUCCESS"
  | "BUSINESS_ERROR"
  | "INVALID_REQUEST";

export interface BulkUploadTextRequestWsDTO {
  csvInputString: string;
}

export interface BulkUploadPreviewContextWsDTO {
  baseSiteId: string;
  soldTo: string;
  salesOrg: string;
  endCustomer?: string;
  targetCartId: string;
}

export interface BulkUploadEntryIntentWsDTO {
  productId: string;
  quantity: number;
  requestDeliveryDate?: string;
  comment?: string;
  priceReference?: string;
  customerOrderNumber?: string;
  targetPrice?: number;
}

export interface BulkUploadAddRequestWsDTO {
  previewContext: BulkUploadPreviewContextWsDTO;
  entries: BulkUploadEntryIntentWsDTO[];
  trackingUid?: string;
}

export interface BulkUploadMessageWsDTO {
  code: string;
  type: "INFO" | "ERROR";
}

export interface BulkUploadPreviewProductWsDTO {
  targetPrice?: number;
  housingProduct?: boolean;
  isPMA?: boolean;
  comment?: string;
  matnr?: string;
  messages?: BulkUploadMessageWsDTO[];
  productCode?: string;
  productId: string;
  quantity: number;
  requestDeliveryDate?: string;
  priceReference?: string;
  quantityVisible?: boolean;
  type?: ProductMarketType;
  succeedingProductDesignation?: string;
  succeedingProductUrl?: string;
  customerOrderNumber?: string;
  onSale?: boolean;
}

export interface BulkUploadGlobalErrorWsDTO {
  reason: string;
  params?: string[];
}

export interface BulkUploadRowErrorWsDTO {
  rowNumber: number;
  field: string;
  code: string;
  rejectedValue: string;
}

export interface BulkUploadSuccessWsDTO {
  noLines: number;
  productAmount: number;
  quantityAdjusted: boolean;
}

export interface BulkUploadPreviewResponseWsDTO {
  outcome: BulkUploadOutcome;
  trackingUid?: string;
  previewContext?: BulkUploadPreviewContextWsDTO;
  products?: BulkUploadPreviewProductWsDTO[];
  globalError?: BulkUploadGlobalErrorWsDTO;
  rowErrors?: BulkUploadRowErrorWsDTO[];
}

export interface BulkUploadAddResponseWsDTO extends BulkUploadPreviewResponseWsDTO {
  success?: BulkUploadSuccessWsDTO;
}

export interface BulkUploadMessage {
  code: string;
  type: "info" | "error";
  params: Record<string, string>;
}

export interface BulkUploadPreviewProduct {
  targetPrice: number | null;
  housingProduct: boolean;
  isPMA: boolean;
  comment: string;
  matnr: string;
  messages: BulkUploadMessage[];
  productCode: string;
  productId: string;
  quantity: number;
  requestDeliveryDate: string | null;
  priceReference: string;
  quantityVisible: boolean;
  type: ProductMarketType | null;
  succeedingProductDesignation: string;
  succeedingProductUrl: string;
  customerOrderNumber: string;
  onSale: boolean;
}

export interface BulkUploadGlobalError {
  reason: string;
  params: string[];
}

interface BulkUploadResponse {
  outcome: BulkUploadOutcome;
  trackingUid?: string;
  previewContext?: BulkUploadPreviewContextWsDTO;
  products: BulkUploadPreviewProduct[];
  globalError?: BulkUploadGlobalError;
  rowErrors: BulkUploadRowErrorWsDTO[];
}

export type BulkUploadPreviewResponse = BulkUploadResponse;

export interface BulkUploadAddResponse extends BulkUploadResponse {
  success?: BulkUploadSuccessWsDTO;
}

export interface BulkUploadTemplate {
  blob: Blob;
  fileName: string;
  contentType: string;
  contentLength?: number;
}

export interface BulkUploadWorkflowOperations<TMode extends BulkUploadMode, TPreviewInput, TSubmitInput> {
  readonly mode: TMode;
  preview(input: TPreviewInput, signal?: AbortSignal): Promise<BulkUploadPreviewResponse>;
  submit(input: TSubmitInput, signal?: AbortSignal): Promise<BulkUploadAddResponse>;
}

export function normalizeBulkUploadPreviewResponse(
  response: BulkUploadPreviewResponseWsDTO,
): BulkUploadPreviewResponse {
  return normalizeBulkUploadResponse(response);
}

export function normalizeBulkUploadAddResponse(response: BulkUploadAddResponseWsDTO): BulkUploadAddResponse {
  return {
    ...normalizeBulkUploadResponse(response),
    ...(response.success ? { success: response.success } : {}),
  };
}

function normalizeBulkUploadResponse(response: BulkUploadPreviewResponseWsDTO): BulkUploadResponse {
  return {
    outcome: response.outcome,
    ...(response.trackingUid ? { trackingUid: response.trackingUid } : {}),
    ...(response.previewContext ? { previewContext: response.previewContext } : {}),
    products: (response.products ?? []).map(normalizeBulkUploadPreviewProduct),
    ...(response.globalError
      ? {
          globalError: {
            reason: response.globalError.reason,
            params: response.globalError.params ?? [],
          },
        }
      : {}),
    rowErrors: response.rowErrors ?? [],
  };
}

function normalizeBulkUploadPreviewProduct(product: BulkUploadPreviewProductWsDTO): BulkUploadPreviewProduct {
  return {
    targetPrice: product.targetPrice === 0 ? null : product.targetPrice ?? null,
    housingProduct: product.housingProduct ?? false,
    isPMA: product.isPMA ?? false,
    comment: product.comment ?? "",
    matnr: product.matnr ?? "",
    messages: (product.messages ?? []).map((message) => ({
      code: message.code,
      type: message.type === "INFO" ? "info" : "error",
      params: {},
    })),
    productCode: product.productCode ?? "",
    productId: product.productId,
    quantity: product.quantity,
    requestDeliveryDate: product.requestDeliveryDate ?? null,
    priceReference: product.priceReference ?? "",
    quantityVisible: product.quantityVisible ?? true,
    type: product.type ?? null,
    succeedingProductDesignation: product.succeedingProductDesignation ?? "",
    succeedingProductUrl: product.succeedingProductUrl ?? "",
    customerOrderNumber: product.customerOrderNumber ?? "",
    onSale: product.onSale ?? false,
  };
}
